import { useState, Suspense, lazy } from 'react';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { useAuthStore } from '@/store/auth.store';
import { ROLE_TO_NAV_KEY } from '@/types/auth.types';
import { LoadingSpinner } from '@/components/feedback/LoadingSpinner';
import { ErrorBoundary } from '@/components/feedback/ErrorBoundary';

// Lazy-loaded page components for code-splitting
const DocumentQueuePage = lazy(() => import('@/features/documents/components/DocumentQueue'));
const DocumentDetailPage = lazy(() => import('@/features/documents/components/DocumentDetail'));
const QCReviewPage = lazy(() => import('@/features/qc/components/QCReviewLayout'));
const ProofreadPage = lazy(() => import('@/features/qc/components/ProofreadView'));
const AuditLogPage = lazy(() => import('@/features/admin/components/AuditLogTable'));
const RulesPage = lazy(() => import('@/features/config/components/ValidationRuleEditor'));
const ProofReaderWorkspacePage = lazy(() => import('@/features/qc/components/ProofReaderWorkspace'));
const SetupPage = lazy(() => import('@/features/setup/components/SetupPage'));

const ROLE_DEFAULTS: Record<string, string> = {
  coord: 'intake',
  spec: 'workspace',
  qc: 'qc',
  proof: 'intake',
};

const SuspenseFallback = (
  <div style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
    <LoadingSpinner size={28} />
  </div>
);

export function AppShell() {
  const user = useAuthStore((s) => s.user);
  const roleKey = user ? ROLE_TO_NAV_KEY[user.role] : 'coord';
  const [page, setPage] = useState(ROLE_DEFAULTS[roleKey] || 'intake');
  const [activeJob, setActiveJob] = useState<unknown>(null);

  const [intakeFilter, setIntakeFilter] = useState<string | undefined>(undefined);

  const navigate = (p: string, payload?: unknown) => {
    setPage(p);
    // Persist the selected job for any page that operates on a single document.
    // Previously only 'workspace' was supported, which caused QCReviewLayout to
    // render with job=null and silently no-op on "Mark QC complete".
    if (p === 'workspace' || p === 'qc' || p === 'proofread') {
      setActiveJob(payload || null);
    }
    // Allow callers to deep-link into the intake queue with a pre-selected
    // status filter — e.g. after QC completion, surface the completed bucket.
    if (p === 'intake') {
      const f = (payload && typeof payload === 'object' && 'filter' in (payload as any))
        ? ((payload as any).filter as string | undefined)
        : undefined;
      setIntakeFilter(f);
    }
  };

  const renderPage = () => {
    switch (page) {
      case 'intake':
        return <DocumentQueuePage go={navigate} initialFilter={intakeFilter} />;
      case 'workspace':
        if (user?.role === 'proofreader') {
          return <ProofReaderWorkspacePage job={activeJob} go={navigate} />;
        }
        return <DocumentDetailPage job={activeJob} go={navigate} />;
      case 'qc':
        return <QCReviewPage go={navigate} job={activeJob} />;
      case 'proofread':
        return <ProofreadPage go={navigate} job={activeJob} />;
      case 'audit':
        return <AuditLogPage go={navigate} />;
      case 'rules':
        return <RulesPage go={navigate} />;
      case 'setup':
        return <SetupPage go={navigate} />;
      default:
        return <DocumentQueuePage go={navigate} />;
    }
  };

  return (
    <div className="app">
      <Sidebar currentPage={page} onNavigate={navigate} />
      <main className="main">
        <Topbar currentPage={page} />
        <div className="content" key={page}>
          <ErrorBoundary>
            <Suspense fallback={SuspenseFallback}>{renderPage()}</Suspense>
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
}
